import React from 'react'
import { Box, Icon, Highlight, Flex, Text, Tag } from '@chakra-ui/react'
import { FaPaw } from 'react-icons/fa'
import { FiUser } from 'react-icons/fi'
import { get as leven } from 'fast-levenshtein'

import {
    ClientPreviewFieldsFragment,
    ClinicFieldsFragment,
    QueryClientsPatientsQuery,
} from '~graphql/generated/graphql'
import {
    formatEmail,
    getAlivePatients,
    getClientFullName,
    getClientSecondaryName,
} from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import { useClinic } from '~contexts/clinic-context'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { FaEnvelope, FaPhone } from 'react-icons/fa6'
import ClientPortalAppTags from '~components/shared/tags/client-portal-app-tags'

type ClientSearchResultProps = Readonly<{
    res: QueryClientsPatientsQuery['queryClientsPatients']['results'][0]
    value: string
    onClick: (client: ClientPreviewFieldsFragment) => void
    clinic: ClinicFieldsFragment
}>

export default function ClientSearchResult({
    res,
    value,
    onClick,
    clinic,
}: ClientSearchResultProps) {
    const client = res.client

    if (!client) return <></>

    const MAX_PATIENTS_TO_DISPLAY = 3

    const totalPatientCount = res.patients.length

    const patients = [...getAlivePatients(res.patients)]
        .sort((a, b) => leven(value, a.name || '') - leven(value, b.name || ''))
        .slice(0, MAX_PATIENTS_TO_DISPLAY)

    const isInactive = !client.is_active

    const clientSecondaryName = getClientSecondaryName(client)

    return (
        <Box
            key={client.id}
            as="button"
            w="100%"
            px={4}
            py={2}
            borderRadius={8}
            _hover={{
                bg: 'gray.100',
            }}
            opacity={isInactive ? 0.8 : 1}
            onClick={() => onClick(client)}
        >
            <SharedStack row textAlign="left" spacing={4}>
                <Icon as={FiUser} boxSize={6} />
                <Box>
                    <SharedStack row>
                        <Text fontSize="sm" fontWeight="semibold">
                            <Highlight
                                styles={{
                                    bg: 'brand.100',
                                }}
                                query={value}
                            >
                                {getClientFullName(client) || ''}
                            </Highlight>
                        </Text>
                        {clientSecondaryName && (
                            <Text fontSize="xs" color="gray.500" mb={-1}>
                                <Highlight
                                    styles={{ bg: 'brand.100' }}
                                    query={value}
                                >
                                    {`(secondary: ${clientSecondaryName})`}
                                </Highlight>
                            </Text>
                        )}
                        {!client.is_active && (
                            <Tag size="sm" colorScheme="red">
                                Inactive
                            </Tag>
                        )}
                        {!client.pms_id && (
                            <Tag size="sm" colorScheme="yellow">
                                Non-system
                            </Tag>
                        )}
                        <ClientPortalAppTags client={client} />
                    </SharedStack>
                    <SharedStack row spacing={4}>
                        <SharedStack row>
                            <Icon
                                as={FaEnvelope}
                                fontSize="xs"
                                color={!client.email ? 'gray.400' : 'gray.600'}
                            />
                            <Text
                                fontSize="xs"
                                color={!client.email ? 'gray.400' : 'gray.500'}
                            >
                                <Highlight
                                    query={value}
                                    styles={{
                                        bg: 'brand.100',
                                    }}
                                >
                                    {formatEmail(client.email) || 'N/A'}
                                </Highlight>
                            </Text>
                        </SharedStack>
                        <SharedStack row>
                            <Icon as={FaPhone} fontSize="xs" color="gray.600" />
                            <Text
                                fontSize="xs"
                                color="gray.500"
                                whiteSpace="nowrap"
                            >
                                <Highlight
                                    query={value}
                                    styles={{ bg: 'brand.100' }}
                                >
                                    {formatPhoneReadable(
                                        client.phone,
                                        client.address?.country ||
                                            clinic?.address?.country,
                                    ) || ''}
                                </Highlight>
                            </Text>
                        </SharedStack>
                    </SharedStack>
                </Box>
            </SharedStack>
            <Flex flexWrap="wrap" rowGap={1} columnGap={4}>
                {patients.map(p => (
                    <SharedStack row fit key={p.id}>
                        <Icon as={FaPaw} boxSize={4} />
                        <Text fontSize="sm">
                            <Highlight
                                styles={{
                                    bg: 'brand.100',
                                }}
                                query={value}
                                key={p.id}
                            >
                                {p.name || ''}
                            </Highlight>
                        </Text>
                    </SharedStack>
                ))}
                {totalPatientCount > MAX_PATIENTS_TO_DISPLAY && (
                    <Text fontSize="sm" color="gray.500">
                        {`+ ${
                            totalPatientCount - MAX_PATIENTS_TO_DISPLAY
                        } more`}
                    </Text>
                )}
            </Flex>
        </Box>
    )
}
